import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import useClearProfile from 'hooks/auth/useClearProfile';
import useCsrf from 'hooks/auth/useCsrf';
import useImpersonate from 'hooks/auth/useImpersonate';
import useLogin from 'hooks/auth/useLogin';
import useLogout from 'hooks/auth/useLogout';
import useSelectProfile from 'hooks/auth/useSelectProfile';
import useSession from 'hooks/auth/useSession';
import useStopImpersonation from 'hooks/auth/useStopImpersonation';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Login, Session, SessionStaff } from 'types';
import { setSessionInvalidationCallback } from 'utils/api-client';
import { normalizeTimezone } from 'utils/date-functions';
import notificationHandler from 'utils/notifications';
import { SessionSyncEvent, SessionSyncEventType, sessionSyncManager } from 'utils/session-sync';

interface ProviderProps {
  session: Session | null;
  isLoading: boolean;
  refreshMe: () => void;
  login: (payload: Login) => void;
  logout: () => void;
  /**
   * Logs out and stays on the current page instead of going to the login screen.
   * Resolves `true` once the session is gone, `false` when the request failed.
   */
  logoutInPlace: () => Promise<boolean>;
  selectProfile: (profileId: string) => void;
  selectProfileAsync: (profileId: string) => Promise<void>;
  clearProfile: () => void;
  impersonate: (profileId: string) => void;
  stopImpersonation: () => void;
  hasPerm: (permission: string) => boolean;
}

const AuthContext = createContext<ProviderProps>({
  session: null,
  isLoading: false,
  refreshMe: () => {},
  login: () => {},
  logout: () => {},
  logoutInPlace: () => Promise.resolve(false),
  selectProfile: () => {},
  selectProfileAsync: () => Promise.resolve(),
  clearProfile: () => {},
  impersonate: () => {},
  stopImpersonation: () => {},
  hasPerm: () => false,
});

const AuthProvider = ({ children }: { children: React.ReactNode }): any => {
  const queryClient = useQueryClient();
  const { errorNotification } = notificationHandler();
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const selectProfileMutation = useSelectProfile();
  const clearProfileMutation = useClearProfile();
  const impersonationMutation = useImpersonate();
  const stopImpersonationMutation = useStopImpersonation();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { data: sessionData, isSuccess, isError, refetch: refreshMe } = useSession();
  useCsrf();

  const isHandlingSyncEvent = useRef(false);
  const previousGymIdRef = useRef<string | null | undefined>(undefined);

  const login = useCallback(
    ({ email, password }: Login & { navigateAfterLogin?: boolean }): void => {
      setIsLoading(true);
      loginMutation.mutate(
        { email, password },
        {
          onSuccess: () => {
            refreshMe();
            sessionSyncManager.broadcast(SessionSyncEventType.LOGIN);
          },
          onError: error => {
            errorNotification('Login failed', error.response?.data.message);
            refreshMe();
          },
        },
      );
    },
    [errorNotification, loginMutation, refreshMe],
  );

  /**
   * Internal logout function that clears state and navigates
   * Can be called directly or via API-triggered session invalidation
   */
  const performLogout = useCallback(
    (skipBroadcast = false, redirectToLogin = true): void => {
      queryClient.clear();
      setSession(null);
      if (redirectToLogin) navigate('/login');

      if (!skipBroadcast) {
        sessionSyncManager.broadcast(SessionSyncEventType.LOGOUT);
      }
    },
    [navigate, queryClient],
  );

  const requestLogout = useCallback(
    (redirectToLogin: boolean, onSettled?: (succeeded: boolean) => void): void => {
      logoutMutation.mutate(null, {
        onSuccess: () => {
          performLogout(false, redirectToLogin);
          onSettled?.(true);
        },
        onError: () => {
          errorNotification('Logout failed');
          refreshMe();
          onSettled?.(false);
        },
      });
    },
    [errorNotification, refreshMe, logoutMutation, performLogout],
  );

  const logout = useCallback((): void => requestLogout(true), [requestLogout]);
  const logoutInPlace = useCallback(
    (): Promise<boolean> => new Promise(resolve => requestLogout(false, resolve)),
    [requestLogout],
  );

  const selectProfile = useCallback(
    (profileId: string): void => {
      selectProfileMutation.mutate(
        { profileId },
        {
          onError: res => {
            errorNotification('Profile selection failed', res.response?.data.message);
          },
          onSettled: () => {
            refreshMe();
          },
        },
      );
    },
    [errorNotification, refreshMe, selectProfileMutation],
  );

  const selectProfileAsync = useCallback(
    async (profileId: string): Promise<void> => {
      await selectProfileMutation.mutateAsync(
        { profileId },
        {
          onError: res => {
            errorNotification('Profile selection failed', res.response?.data.message);
          },
          onSettled: () => {
            refreshMe();
          },
        },
      );
    },
    [errorNotification, refreshMe, selectProfileMutation],
  );

  const clearProfile = useCallback((): void => {
    clearProfileMutation.mutate(null, {
      onSuccess: () => {
        queryClient.clear();
      },
      onError: () => {
        errorNotification('Something failed');
      },
      onSettled: () => {
        refreshMe();
      },
    });
  }, [clearProfileMutation, errorNotification, queryClient, refreshMe]);

  const impersonate = useCallback(
    (profileId: string): void => {
      impersonationMutation.mutate(
        { profileId },
        {
          onSuccess: () => {
            queryClient.clear();
          },
          onError: res => {
            errorNotification('Impersonation failed', res.response?.data.message);
          },
          onSettled: () => {
            refreshMe();
          },
        },
      );
    },
    [errorNotification, impersonationMutation, queryClient, refreshMe],
  );

  const stopImpersonation = useCallback((): void => {
    stopImpersonationMutation.mutate(null, {
      onSuccess: () => {
        queryClient.clear();
      },
      onError: () => {
        errorNotification('Something failed');
      },
      onSettled: () => {
        refreshMe();
      },
    });
  }, [errorNotification, queryClient, refreshMe, stopImpersonationMutation]);

  const hasPerm = useCallback(
    (permission: string): boolean => {
      if (!session) return false;
      if (session.athlete) return true;
      if (session.staff) {
        if (session.role === 'owner') return true;
        return session.staff.permissions.includes(permission as SessionStaff['permissions'][number]);
      }
      if (session.kiloStaff) return true;
      return false;
    },
    [session],
  );

  const contextValue = useMemo(
    () => ({
      session,
      isLoading,
      login,
      logout,
      logoutInPlace,
      refreshMe,
      hasPerm,
      selectProfile,
      selectProfileAsync,
      clearProfile,
      impersonate,
      stopImpersonation,
    }),
    [
      session,
      isLoading,
      login,
      logout,
      logoutInPlace,
      refreshMe,
      hasPerm,
      selectProfile,
      selectProfileAsync,
      clearProfile,
      impersonate,
      stopImpersonation,
    ],
  );

  useEffect(() => {
    if (isSuccess && sessionData?.data) {
      const gymId = sessionData.data.gym?.gymId ?? null;
      const previousGymId = previousGymIdRef.current;
      const hasSwitchedBetweenGyms =
        previousGymId !== null && previousGymId !== undefined && gymId !== null && previousGymId !== gymId;

      if (hasSwitchedBetweenGyms) {
        queryClient.removeQueries({ predicate: query => query.queryKey[0] !== 'session' });
      }
      previousGymIdRef.current = gymId;

      const nextSession = sessionData.data;
      setSession(
        nextSession.gym
          ? {
              ...nextSession,
              gym: { ...nextSession.gym, timezone: normalizeTimezone(nextSession.gym.timezone) },
            }
          : nextSession,
      );
      setIsLoading(false);
    }
  }, [isSuccess, queryClient, sessionData?.data]);

  useEffect(() => {
    if (isError) {
      setSession(null);
      setIsLoading(false);
    }
  }, [isError]);

  // Set up cross-tab session synchronization
  useEffect(() => {
    sessionSyncManager.initialize();
    setSessionInvalidationCallback(() => {
      if (!isHandlingSyncEvent.current) {
        isHandlingSyncEvent.current = true;
        performLogout(true); // Skip broadcast since API client already broadcasted
        setTimeout(() => {
          isHandlingSyncEvent.current = false;
        }, 100);
      }
    });

    const unsubscribe = sessionSyncManager.subscribe((event: SessionSyncEvent) => {
      if (isHandlingSyncEvent.current) {
        return;
      }

      isHandlingSyncEvent.current = true;

      switch (event.type) {
        case SessionSyncEventType.LOGOUT:
        case SessionSyncEventType.SESSION_INVALIDATED:
          performLogout(true); // Skip broadcast since this was triggered by a broadcast
          break;
        case SessionSyncEventType.LOGIN:
          refreshMe();
          break;
      }

      setTimeout(() => {
        isHandlingSyncEvent.current = false;
      }, 100);
    });

    return () => {
      unsubscribe();
      sessionSyncManager.cleanup();
    };
  }, [performLogout, refreshMe]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && session) {
        refreshMe();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleFocus = (): void => {
      if (session) {
        refreshMe();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [session, refreshMe]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): ProviderProps => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
};

export default AuthProvider;
